import { jsx as _jsx } from "react/jsx-runtime";
import { MaterialDesignContent, SnackbarProvider as NotistackProvider, useSnackbar, } from 'notistack';
import { forwardRef } from 'react';
import { useBaseConfig } from '@zurich/common/contexts';
import { font } from '../../styles';
import { styled } from '../../utils';
import { Icon } from '../Icon';
import { IconButton } from '../IconButton';
export function SnackbarProvider({ children }) {
    return (_jsx(NotistackProvider, { Components: {
            default: SnackbarContent,
            success: SnackbarContent,
            error: SnackbarContent,
            warning: SnackbarContent,
            info: SnackbarContent,
        }, iconVariant: {
            default: _jsx(SnackbarLoadingSpinnerStyled, {}),
            error: _jsx(SnackbarIcon, { variant: "face-sad" }),
            info: _jsx(SnackbarIcon, { variant: "face-smile" }),
            success: _jsx(SnackbarIcon, { variant: "face-laugh" }),
            warning: _jsx(SnackbarIcon, { variant: "face-neutral" }),
        }, maxSnack: 3, autoHideDuration: 5000, preventDuplicate: true, children: children }));
}
function SnackbarCloseButton({ id }) {
    const { closeSnackbar } = useSnackbar();
    return (_jsx(IconButton, { variant: "close", color: "inherit", size: "small", onClick: () => closeSnackbar(id) }));
}
const SnackbarContent = forwardRef(function SnackbarContent(props, ref) {
    const { closeSnackbar } = useSnackbar();
    const { debug } = useBaseConfig();
    const { id, action: customAction, variant, onClick } = props;
    // per default we provide a close button to errors (so the user can copy the error message)
    const explicitCloseButton = variant === 'error';
    const action = customAction ??
        (explicitCloseButton
            ? (key) => _jsx(SnackbarCloseButton, { id: key })
            : undefined);
    return (_jsx("div", { onClick: (e) => {
            if (!explicitCloseButton) {
                e.stopPropagation();
                closeSnackbar(id);
            }
            onClick?.();
        }, children: _jsx(MaterialDesignContentStyled, { ...props, ref: ref, action: action, isdebug: debug }) }));
});
const MaterialDesignContentStyled = styled(MaterialDesignContent, {
    shouldForwardProp: (prop) => prop !== 'isdebug',
})(({ theme, isdebug }) => ({
    cursor: isdebug ? 'pointer' : 'inherit',
    '&.notistack-MuiContent': {
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'nowrap',
        oxShadow: theme.shadows[3],
        fontSize: font.size.s,
        lineHeight: font.lineHeight.s,
        letterSpacing: font.letterSpacing.m,
        fontWeight: font.weight.regular,
        backgroundColor: `${theme.palette.secondary.contrastText}`,
        color: `${theme.palette.primary.dark}`,
        [theme.breakpoints.up('lg')]: {
            fontSize: font.size.m,
            letterSpacing: font.letterSpacing.m,
            fontWeight: font.weight.light,
        },
    },
    '& #notistack-snackbar': {
        flexWrap: 'nowrap',
        gap: theme.spacing(1),
    },
    '&.notistack-MuiContent-success': {
        backgroundColor: `${theme.palette.success.light}`,
        color: theme.palette.common.white,
    },
    '&.notistack-MuiContent-error': {
        backgroundColor: `${theme.palette.error.main}`,
        color: theme.palette.common.white,
    },
    '&.notistack-MuiContent-warning': {
        backgroundColor: `${theme.palette.warning.light}`,
        color: theme.palette.common.white,
    },
    '&.notistack-MuiContent-info': {
        backgroundColor: `${theme.palette.primary.main}`,
        color: `${theme.palette.primary.contrastText}`,
    },
}));
const SnackbarIcon = styled(Icon)({
    '&.MuiIcon-root': {
        paddingRight: 0,
        width: 'auto',
        height: 'auto',
    },
});
const SnackbarLoadingSpinnerStyled = styled('div')(({ theme }) => ({
    '@keyframes loadingSpinnerAnimation': {
        to: {
            transform: 'rotate(1turn)',
        },
    },
    width: theme.spacing(4),
    height: theme.spacing(4),
    borderRadius: '50%',
    border: '4px solid',
    borderColor: theme.palette.groupPrimary.lightDove,
    margin: theme.spacing(1, 2, 1, -0.5),
    borderRightColor: theme.palette.primary.main,
    borderTopColor: theme.palette.primary.main,
    borderBottomColor: theme.palette.primary.main,
    animation: 'loadingSpinnerAnimation 1s infinite linear',
}));
